/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Alert,
  AlertIcon,
  Text,
  VStack,
  HStack,
  Icon,
  Center,
  FormLabel,
  Code,
} from '@chakra-ui/react';
import React, { useState } from 'react';
// import Button from "../common/Button";
// import useCustomToast from "../common/Toast";
import { AlertTriangle, Clock, Database, Shield, Trash2 } from 'lucide-react';
import { FiAlertTriangle } from 'react-icons/fi';
// import { useDeleteOnboardingData } from "@/services/settings";
import { useNavigate } from 'react-router-dom';
import { useGetLoggedInUserData } from '@/services/auth/auth';
import Button from '@/components/common/Button';
import useCustomToast from '@/components/common/Toast';
import { useDeleteAccount } from '@/services/account-setting';
import { NAVIGATION_ROUTES } from '@/routes/constants';

// Terms and conditions
const terms = [
  {
    icon: Database,
    title: 'Data Loss',
    desc: 'All your personal data, settings, and content will be permanently deleted from our servers.',
  },
  {
    icon: Clock,
    title: 'Irreversible Action',
    desc: 'This action cannot be undone. Once deleted, your account cannot be recovered.',
  },
  {
    icon: Shield,
    title: 'Service Termination',
    desc: 'All associated services, subscriptions, and access will be immediately terminated.',
  },
];

// Utility to capitalize first word
const capitalizeFirstWord = (str: string | undefined) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void; // Callback for final deletion
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const { data: user } = useGetLoggedInUserData();
  const { mutate: deleteAccount, isLoading } = useDeleteAccount();
  const { showToast } = useCustomToast();
  const [step, setStep] = useState(1);
  const [confirmationText, setConfirmationText] = useState('');
  const navigate = useNavigate();

  const handleNextStep = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      deleteAccount(undefined, {
        onSuccess: () => {
          localStorage.removeItem('authStorage');
          localStorage.removeItem('auth');
          showToast('success', `User has been deleted successfully!`);
          onConfirm?.();
          setStep(1);
          setConfirmationText('');
          onClose();
          navigate(NAVIGATION_ROUTES.SIGNIN);
        },
        onError: (error: unknown) => {
          showToast('error', (error as any)?.response?.data?.message || 'Failed to delete user.');
        },
      });
    }
  };

  const handleClose = () => {
    setStep(1);
    setConfirmationText('');
    onClose();
  };

  const renderModalBody = () => {
    switch (step) {
      case 1:
        return (
          <VStack spacing={6} textAlign="center" py={4}>
            <Center w="80px" h="80px" bg="red.50" borderRadius="full">
              <Icon as={AlertTriangle} w="40px" h="40px" color="red.400" />
            </Center>

            <VStack spacing={3}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.900">
                Delete {capitalizeFirstWord(user?.first_name)}{' '}
                {capitalizeFirstWord(user?.last_name)}'s Account?
              </Text>
              <Text color="gray.600" lineHeight="relaxed" maxW="md" fontSize="md">
                We understand that sometimes accounts need to be removed. This action will
                permanently delete your account and all associated data. Please take a moment to
                consider this decision.
              </Text>
            </VStack>
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={6} py={2}>
            <Alert
              status="warning"
              bg="yellow.50"
              border="1px solid"
              borderColor="yellow.200"
              borderRadius="md"
              p={4}
              alignItems="flex-start"
            >
              <AlertIcon as={FiAlertTriangle} color="yellow.500" boxSize={5} mt={1} />

              <Box ml={3}>
                <Text fontWeight="semibold" color="yellow.800" mb={1}>
                  Important Notice
                </Text>
                <Text fontSize="sm" color="yellow.700">
                  Please review the following carefully. These changes will take effect immediately.
                </Text>
              </Box>
            </Alert>

            <VStack spacing={4} w="full">
              {terms.map((item, index) => (
                <Flex
                  key={index}
                  align="flex-start"
                  p={4}
                  bg="gray.50"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.100"
                  w="full"
                >
                  <Center w="40px" h="40px" bg="white" borderRadius="lg" mr={4} flexShrink={0}>
                    <Icon as={item.icon} w="20px" h="20px" color="gray.600" />
                  </Center>
                  <Box>
                    <Text fontWeight="medium" color="gray.900" mb={1}>
                      {item.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="relaxed">
                      {item.desc}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </VStack>
          </VStack>
        );

      case 3:
        return (
          <VStack spacing={6} py={2}>
            <VStack spacing={4} textAlign="center">
              <Center w="64px" h="64px" bg="red.100" borderRadius="full">
                <Icon as={Trash2} w="32px" h="32px" color="red.500" />
              </Center>

              <VStack spacing={3}>
                <Text fontSize="xl" fontWeight="bold" color="gray.900">
                  Final Confirmation
                </Text>
                <Text color="gray.600" mb={2}>
                  To proceed with account deletion, please type{' '}
                  <Code
                    fontFamily="mono"
                    fontWeight="semibold"
                    bg="gray.100"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    DELETE
                  </Code>{' '}
                  below.
                </Text>
              </VStack>
            </VStack>

            <Box w="full" textAlign="left">
              <FormLabel
                htmlFor="confirmation"
                fontSize="sm"
                fontWeight="medium"
                color="gray.700"
                mb={2}
              >
                Confirmation Text
              </FormLabel>
              <Input
                id="confirmation"
                type="text"
                placeholder="Type DELETE to confirm"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                px={4}
                py={3}
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                fontFamily="mono"
                _focus={{
                  ring: 2,
                  ringColor: 'red.500',
                  borderColor: 'red.500',
                }}
                transition="all 0.2s"
              />
            </Box>
          </VStack>
        );

      default:
        return null;
    }
  };

  const renderFooterButtonText = () => {
    switch (step) {
      case 1:
        return 'I want to delete this account';
      case 2:
        return 'I have understood the terms and conditions';
      case 3:
        return 'Delete Account';
      default:
        return 'Next';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(2px)" />

      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader
          fontFamily="primary"
          borderBottom="1px solid"
          borderBottomColor="border.default"
          py={6}
          px={6}
        >
          <Flex align="center" gap={2}>
            <Text fontSize="lg" fontWeight="semibold">
              Delete {user?.first_name} {user?.last_name}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Step {step} of 3
            </Text>
          </Flex>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody
          borderBottom="1px solid"
          borderBottomColor="border.default"
          fontFamily="primary"
          px={6}
          py={4}
        >
          {renderModalBody()}
        </ModalBody>

        <ModalFooter fontFamily="primary" bg="gray.50" px={6} py={6}>
          <HStack spacing={3} w="full">
            {/* <Button
              flex={1}
              variant="outline"
              onClick={handleClose}
              color="gray.700"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              _hover={{
                bg: "gray.50"
              }}
            >
              Cancel
            </Button> */}
            <Button
              flex={1}
              onClick={handleNextStep}
              isLoading={isLoading}
              bg={step === 3 ? 'brand.primary.800' : 'background.tertiary'}
              _hover={{
                bg: 'brand.primary.DEFAULT',
                color: 'text.white',
              }}
              color={step === 3 ? 'text.white' : 'text.primary'}
              isDisabled={step === 3 && confirmationText !== 'DELETE'}
            >
              {renderFooterButtonText()}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteAccountModal;
